import { DateValue } from '@internationalized/date';
import { PlusIcon } from '@persona-id/design-tokens/icons';
import { RangeValue } from '@react-types/shared';
import { isEmpty } from 'lodash';
import { DateTime } from 'luxon';
import { useCallback, useMemo, VFC } from 'react';
import { Helmet } from 'react-helmet-async';

import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import Pagination from 'dashboard/components/Pagination';
import WorkflowManualRunModal from 'dashboard/components/WorkflowManualRunModal';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { Environment } from 'dashboard/models/environment';
import { Workflow } from 'dashboard/models/workflow';
import { WorkflowRun, WorkflowRunStatus } from 'dashboard/models/workflow-run';
import { WorkflowRunSimulated } from 'dashboard/models/workflow-run-simulated';
import { WorkflowVersion } from 'dashboard/models/workflow-version';
import { WorkflowRunsFilterParams } from 'dashboard/redux/actions/workflow-runs';
import { ManualRunResults } from 'dashboard/redux/reducers/workflow-runs';
import WorkflowRunsConfigurationBar from 'dashboard/views/DashboardWorkflowRunsView/WorkflowRunsConfigurationBar';
import WorkflowRunsRerunModal from 'dashboard/views/DashboardWorkflowRunsView/WorkflowRunsRerunModal';
import WorkflowRunsTable, {
  CellType,
  ColumnConfig,
} from 'dashboard/views/DashboardWorkflowRunsView/WorkflowRunsTable';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import { LocalStorageKey } from 'lib/clients/StorageClient';
import { TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useLocalStorage from 'lib/hooks/useLocalStorage';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel, PaginationParams } from 'lib/interfaces/api';
import { captureMessage } from 'lib/reporting';

// Note: It's important to have this run on the component load because we'll want the Luxon Timezone to be
// set prior to these configs being ran. Otherwise, it will default to the system timezone.
const getColumnConfigs = (
  currentEnvironment: Environment,
  workflowsMap: Record<string, Workflow>,
  sortyByMoreColumns: boolean = false,
): Record<string, ColumnConfig> => ({
  workflowName: {
    type: CellType.Default,
    dataTest: 'workflow-runs-table__workflow-name-header',
    header: 'Workflow',
    getValue: (workflowRun: WorkflowRun | WorkflowRunSimulated) =>
      workflowsMap[workflowRun.relationships.workflow.data.id]?.attributes.name,
  },
  versionId: {
    type: CellType.Id,
    dataTest: 'workflow-runs-table__workflow-id-header',
    header: 'Version ID',
    sortParam: sortyByMoreColumns ? 'workflow_version_id' : undefined,
    getValue: (workflowRun: WorkflowRun | WorkflowRunSimulated) =>
      workflowRun.relationships['workflow-version'].data.id,
  },
  runId: {
    type: CellType.Id,
    dataTest: 'workflow-runs-table__workflow-run-id-header',
    header: 'Run ID',
    sortParam: sortyByMoreColumns ? 'token' : undefined,
    getValue: (workflowRun: WorkflowRun | WorkflowRunSimulated) => workflowRun.id,
  },
  status: {
    type: CellType.Status,
    dataTest: 'workflow-runs-table__status-header',
    header: 'Status',
    sortParam: sortyByMoreColumns ? 'status' : undefined,
    getValue: (workflowRun: WorkflowRun | WorkflowRunSimulated) => workflowRun.attributes.status,
  },
  triggerObjectId: {
    type: CellType.Id,
    dataTest: 'workflow-runs-table__trigger-object-id-header',
    header: 'Trigger Object ID',
    linkToObject: true,
    sortParam: sortyByMoreColumns ? 'trigger_object_id' : undefined,
    getValue: (workflowRun: WorkflowRun | WorkflowRunSimulated) =>
      workflowRun.relationships['trigger-object']?.data?.id || '',
  },
  dateCreated: {
    type: CellType.Default,
    dataTest: 'workflow-runs-table__created_at-header',
    id: 'workflow-runs-table__created_at-header',
    // TODO: [Jonny] Fix this - This is a constant that needs to be reloaded upon application load
    header: `Created at (${DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})`,
    sortParam: 'created_at',
    getValue: (workflowRun: WorkflowRun | WorkflowRunSimulated) => {
      const createdAt = workflowRun.attributes['created-at'];
      // Docs: https://moment.github.io/luxon/#/formatting
      return createdAt
        ? `${DateTime.fromISO(createdAt).toLocaleString(DateTime.DATETIME_MED)}`
        : '';
    },
  },
  dateCompleted: {
    type: CellType.Default,
    dataTest: 'workflow-runs-table__completed_at-header',
    id: 'workflow-runs-table__completed_at-header',
    // TODO: [Jonny] Fix this - This is a constant that needs to be reloaded upon application load
    header: `Completed at (${DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})`,
    sortParam: 'completed',
    getValue: (workflowRun: WorkflowRun | WorkflowRunSimulated) => {
      const completedAt = workflowRun.attributes['completed-at'];
      // Docs: https://moment.github.io/luxon/#/formatting
      return completedAt
        ? `${DateTime.fromISO(completedAt).toLocaleString(DateTime.DATETIME_MED)}`
        : '';
    },
  },
  ...(currentEnvironment?.type === ApiModel.EnvironmentSandbox && {
    simulated: {
      type: CellType.Default,
      dataTest: 'workflow-runs-table__simulated-header',
      header: 'Simulated run',
      sortParam: sortyByMoreColumns ? 'simulated' : undefined,
      getValue: (workflowRun: WorkflowRun | WorkflowRunSimulated) =>
        workflowRun.type === ApiModel.WorkflowRunSimulated ? 'Yes' : 'No',
    },
  }),
});

interface DashboardWorkflowRunsViewProps {
  clearManualRunResults: () => void;
  manualRunResults?: ManualRunResults;
  workflowRuns: (WorkflowRun | WorkflowRunSimulated)[];
  workflowRunFilters: WorkflowRunsFilterParams;
  workflowId: string | null;
  workflowRunPaginationParams: PaginationParams;
  workflowVersions: WorkflowVersion[];
  workflowsMap: Record<string, Workflow>;
  pageSize: number;
  isLoadingWorkflowRuns: boolean;
  isLoadingWorkflowVersions: boolean;
  isLoadingCreateBulkManualWorkflowRuns: boolean;
  isLoadingBulkResumeWorkflowRuns: boolean;
  showMessage: (message: string, messageType: MessageType, persist?: boolean) => void;
  sortParams: SortParams;
  selectedRows?: Set<string>;
  setSelectedRows?: (selectedRows: Set<string>) => void;
  handleResumeFormSubmit: (runType: string) => void;
  handleBulkCreateManualWorkflowRuns: (objectIds: string[]) => void;
  handlePageLinkClick: (pageNumber: number) => void;
  handlePageSizeClick: (pageSize: number) => void;
  handleDateUpdate: (range: RangeValue<DateValue> | null) => void;
  handleSortUpdate: (sortParams: SortParams) => void;
  handleStatusFilterUpdate: (status: WorkflowRunStatus[]) => void;
  handleVersionFilterUpdate: (versionIds: string[]) => void;
  handleWorkflowIdFilterUpdate: (workflowIds: string[]) => void;
  handleOmniFilterUpdate: (omni: string) => void;
  onLoadMoreVersions?: () => void;
  currentEnvironment: Environment;
}

const DashboardWorkflowRunsView: VFC<DashboardWorkflowRunsViewProps> = ({
  manualRunResults,
  clearManualRunResults,
  workflowRuns,
  workflowRunFilters,
  workflowId,
  workflowRunPaginationParams,
  workflowVersions,
  workflowsMap,
  pageSize,
  isLoadingWorkflowRuns,
  isLoadingWorkflowVersions,
  isLoadingBulkResumeWorkflowRuns,
  isLoadingCreateBulkManualWorkflowRuns,
  sortParams,
  selectedRows,
  setSelectedRows,
  handleBulkCreateManualWorkflowRuns,
  handleResumeFormSubmit,
  handlePageLinkClick,
  handlePageSizeClick,
  handleDateUpdate,
  handleSortUpdate,
  handleStatusFilterUpdate,
  handleVersionFilterUpdate,
  handleWorkflowIdFilterUpdate,
  handleOmniFilterUpdate,
  onLoadMoreVersions,
  showMessage,
  currentEnvironment,
}) => {
  const hasUsedFilters = !Object.values(workflowRunFilters).every(isEmpty);
  const sortyByMoreColumns = useFeatureFlag(
    'dashboard:use-advanced-workflow-run-index-search-frontend',
  );

  const allColumnConfigs = useMemo(
    () => ({ ...getColumnConfigs(currentEnvironment, workflowsMap, sortyByMoreColumns) }),
    [currentEnvironment, workflowsMap, sortyByMoreColumns],
  );

  const columnConfigOptions = useMemo(
    () =>
      Object.keys(allColumnConfigs).map((key) => ({
        label: allColumnConfigs[key].header,
        value: key,
      })),
    [allColumnConfigs],
  );

  const [selectedColumns, _setSelectedColumns] = useLocalStorage<string[]>(
    LocalStorageKey.DashboardWorkflowRunsTableSelectedColumns,
    columnConfigOptions.map((config) => config.value),
  );

  // Ensure that the selected columns are in consistent order when toggling
  const setSelectedColumns = useCallback(
    (columnIds: string[]) => {
      _setSelectedColumns(
        columnIds.sort(
          (a, b) =>
            columnConfigOptions.findIndex((config) => config.value === a) -
            columnConfigOptions.findIndex((config) => config.value === b),
        ),
      );
    },
    [_setSelectedColumns, columnConfigOptions],
  );

  const headerTitle =
    workflowId && workflowsMap[workflowId]?.attributes?.name
      ? `Workflow Runs: ${workflowsMap[workflowId].attributes.name}`
      : 'Workflow Runs';

  const selectedColumnConfigs = useMemo(() => {
    return selectedColumns.reduce<ColumnConfig[]>((acc, column) => {
      if (!allColumnConfigs[column]) {
        captureMessage(`Column missing from column configs`, 'warning', {
          column,
          columnConfigs: Object.keys(allColumnConfigs).join(','),
          workflowId,
        });
        return acc;
      }
      return [...acc, allColumnConfigs[column]];
    }, []);
  }, [allColumnConfigs, selectedColumns, workflowId]);

  const withSelectableRows = selectedRows != null && setSelectedRows != null;
  const [
    showManualTriggerModal,
    { enable: openManualTriggerModal, disable: closeManualTriggerModal },
  ] = useToggle(false);

  const [showManualRerunModal, { enable: openManualRerunModal, disable: closeManualRerunModal }] =
    useToggle(false);

  const handleCloseAllManualRunModal = useCallback(() => {
    if (showManualTriggerModal) closeManualTriggerModal();
    if (showManualRerunModal) closeManualRerunModal();
  }, [
    showManualTriggerModal,
    showManualRerunModal,
    closeManualTriggerModal,
    closeManualRerunModal,
  ]);

  const title = workflowId
    ? `Runs - ${workflowsMap[workflowId]?.attributes?.name || workflowId} - Workflows`
    : 'Runs - Workflows';

  return (
    <>
      <Helmet title={title} />
      <InactiveEnvironmentBanner />
      <Container>
        <ModalWrapper
          isOpen={showManualTriggerModal || showManualRerunModal}
          shouldCloseOnOverlayClick={true}
          shouldCloseOnEsc={true}
          handleCloseModal={handleCloseAllManualRunModal}
        >
          {showManualRerunModal && (
            <WorkflowRunsRerunModal
              onClose={closeManualRerunModal}
              onSubmit={handleResumeFormSubmit}
              isLoading={isLoadingBulkResumeWorkflowRuns}
            />
          )}
          {showManualTriggerModal && (
            <WorkflowManualRunModal
              clearResults={clearManualRunResults}
              onClose={closeManualTriggerModal}
              onSubmit={handleBulkCreateManualWorkflowRuns}
              results={manualRunResults}
              showMessage={showMessage}
              isLoading={isLoadingCreateBulkManualWorkflowRuns}
            />
          )}
        </ModalWrapper>
        <Header title={headerTitle} />
        <Section data-test='workflow-runs-view-v2'>
          <Card>
            <Card.Body>
              <Card.Title
                actions={
                  <>
                    {withSelectableRows && (
                      <>
                        <Button
                          id='workflow-runs-view__re-run-button'
                          variant='basic'
                          size='sm'
                          disabled={selectedRows.size === 0}
                          onClick={openManualRerunModal}
                        >
                          Re-run {selectedRows.size > 0 && `(${selectedRows.size})`}
                        </Button>
                        <Button
                          id='workflow-runs-view__create-new-runs-button'
                          variant='basic'
                          size='sm'
                          prefixIcon={<PlusIcon />}
                          onClick={openManualTriggerModal}
                        >
                          Create new runs
                        </Button>
                      </>
                    )}
                  </>
                }
              >
                Workflow Runs
              </Card.Title>
              <WorkflowRunsConfigurationBar
                workflowId={workflowId}
                workflowsMap={workflowsMap}
                workflowRunFilters={workflowRunFilters}
                workflowVersions={workflowVersions}
                isLoadingWorkflowVersions={isLoadingWorkflowVersions}
                columnConfigOptions={columnConfigOptions}
                columns={selectedColumns}
                handleWorkflowIdFilterUpdate={handleWorkflowIdFilterUpdate}
                handleVersionFilterUpdate={handleVersionFilterUpdate}
                onLoadMoreVersions={onLoadMoreVersions}
                handleStatusFilterUpdate={handleStatusFilterUpdate}
                handleColumnUpdate={setSelectedColumns}
                handleDateUpdate={handleDateUpdate}
                handleOmniFilterUpdate={handleOmniFilterUpdate}
              />

              <WorkflowRunsTable
                workflowRuns={workflowRuns}
                sortParams={sortParams}
                isLoading={
                  isLoadingWorkflowRuns ||
                  isLoadingWorkflowVersions ||
                  isLoadingBulkResumeWorkflowRuns ||
                  isLoadingCreateBulkManualWorkflowRuns
                }
                hasUsedFilters={hasUsedFilters}
                handleSortUpdate={handleSortUpdate}
                columnConfigs={selectedColumnConfigs}
                selectedRows={selectedRows}
                setSelectedRows={setSelectedRows}
              />
              {workflowRuns.length > 0 && (
                <Pagination
                  pageSize={pageSize}
                  paginationParams={workflowRunPaginationParams}
                  handlePageSizeClick={handlePageSizeClick}
                  handlePageLinkClick={handlePageLinkClick}
                />
              )}
            </Card.Body>
          </Card>
        </Section>
      </Container>
    </>
  );
};

export default DashboardWorkflowRunsView;
